/**
 * The Slides stage (plan docs/plans/lms-plan.md 7.2; 6.1; section 13): one
 * slide per screen, Previous and Next buttons (44 px targets, button driven,
 * no swipe), the left and right arrow keys, a progress bar with a marker at
 * every section, a section list drawer and the glossary side sheet.
 *
 * A knowledge check must be answered before Next (and the arrow key, and the
 * drawer) will move past it. The bookmark saves on every slide change
 * (usePlayer). A screen-reader line announces the slide number and heading.
 */
import { useEffect, useState } from 'react';
import { BookA, ChevronLeft, ChevronRight, List, Lock } from 'lucide-react';
import type { CourseContent } from '../../content/types';
import type { UsePlayerResult } from '../../hooks/usePlayer';
import { outlineBtnCls, primaryBtnCls, secondaryBtnCls } from '../../../ui/styles';
import { Drawer } from './Drawer';
import { SlideView } from './SlideView';
import type { DeckSlide } from './deck';
import { microLabelCls, noticeCls, panelCls, stageCls } from './styles';

export interface SlidesStageProps {
  player: UsePlayerResult;
  content: CourseContent;
  versionNo: number;
  changeSummary: string | null;
  /** Called by Continue on the last slide (to the reading, 7.2). */
  onFinish: () => void;
}

function headingOf(entry: DeckSlide | undefined): string {
  if (!entry) return '';
  if (entry.synthetic === 'change_summary') return 'What changed';
  if (entry.synthetic === 'takeaways') return 'Key takeaways';
  const slide = entry.slide;
  if (!slide) return '';
  if (slide.layout === 'knowledge_check') return slide.heading || 'Check your understanding';
  return slide.heading;
}

function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.tagName !== 'string') return false;
  const tag = el.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable === true;
}

export function SlidesStage({ player, content, versionNo, changeSummary, onFinish }: SlidesStageProps) {
  const { deck, index, canNext, canPrev, next, prev } = player;
  const [sectionsOpen, setSectionsOpen] = useState(false);
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const total = deck.slides.length;
  const entry = deck.slides[index];
  const isLast = index === total - 1;
  const blocked = Boolean(entry?.blocking && !player.isAnswered(entry.key));
  const section = entry ? deck.sections[entry.sectionIndex] : undefined;
  const glossary = Array.isArray(content.glossary) ? content.glossary : [];

  // 7.2: keyboard left and right. Ignored while typing, with a modifier, or while a sheet is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (sectionsOpen || glossaryOpen) return;
      if (isTypingTarget(e.target)) return;
      if (e.key === 'ArrowRight') {
        if (next()) e.preventDefault();
      } else if (e.key === 'ArrowLeft') {
        if (prev()) e.preventDefault();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [next, prev, sectionsOpen, glossaryOpen]);

  if (!entry) {
    return (
      <div className={stageCls}>
        <p className="text-sm text-gray-600 dark:text-gray-300">This course has no slides.</p>
      </div>
    );
  }

  return (
    <div data-testid="lms-slides-stage">
      <div className="border-b border-gray-200 bg-white px-4 py-2 dark:border-gray-700 dark:bg-gray-800 sm:px-6 lg:px-8">
        <div className="mx-auto flex w-full max-w-5xl flex-wrap items-center gap-2">
          <button type="button" className={outlineBtnCls()} onClick={() => setSectionsOpen(true)} data-testid="lms-open-sections">
            <List className="h-4 w-4" aria-hidden="true" />
            Sections
          </button>
          <div className="min-w-0 flex-1 truncate text-sm text-gray-600 dark:text-gray-300" data-testid="lms-slide-section">
            {section ? section.title : ''}
          </div>
          {glossary.length > 0 && (
            <button type="button" className={outlineBtnCls()} onClick={() => setGlossaryOpen(true)} data-testid="lms-open-glossary">
              <BookA className="h-4 w-4" aria-hidden="true" />
              Glossary
            </button>
          )}
        </div>
        <div className="mx-auto mt-2 w-full max-w-5xl">
          <div
            role="progressbar"
            aria-label="Slide progress"
            aria-valuemin={1}
            aria-valuemax={total}
            aria-valuenow={index + 1}
            aria-valuetext={`Slide ${index + 1} of ${total}`}
            className="flex h-1.5 w-full gap-0.5"
            data-testid="lms-slide-progress"
          >
            {deck.sections.map((s) => {
              const filled = index >= s.start + s.count ? 1 : index < s.start ? 0 : (index - s.start + 1) / s.count;
              return (
                <div
                  key={s.id}
                  title={s.title}
                  data-testid="lms-progress-section"
                  className="relative h-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"
                  style={{ flexGrow: s.count, flexBasis: 0 }}
                >
                  <div className="h-full bg-primary-600 dark:bg-primary-500" style={{ width: `${Math.round(filled * 100)}%` }} />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className={stageCls}>
        <div className={`${panelCls} min-h-[18rem]`} key={entry.key}>
          <SlideView
            entry={entry}
            changeSummary={changeSummary}
            versionNo={versionNo}
            keyTakeaways={content.key_takeaways}
            answer={player.answers[entry.key]}
            onSelect={(optionId, multi) => player.selectOption(entry.key, optionId, multi)}
            onCheck={() => player.checkAnswer(entry.key)}
            onChoose={(optionId) => player.chooseScenario(entry.key, optionId)}
          />
        </div>

        {player.saveError && (
          <div className={`${noticeCls('amber')} mt-3`} role="status" data-testid="lms-save-error">
            {player.saveError}
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <button type="button" className={secondaryBtnCls} onClick={() => prev()} disabled={!canPrev} data-testid="lms-slide-prev">
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            Previous
          </button>
          <span className="text-xs tabular-nums text-gray-500 dark:text-gray-400" data-testid="lms-slide-count">
            {`Slide ${index + 1} of ${total}`}
          </span>
          {isLast ? (
            <button type="button" className={primaryBtnCls} onClick={onFinish} disabled={blocked} data-testid="lms-slide-next">
              Continue to the reading
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          ) : (
            <button type="button" className={primaryBtnCls} onClick={() => next()} disabled={!canNext} data-testid="lms-slide-next">
              Next
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
        <p className="sr-only" aria-live="polite" data-testid="lms-slide-announce">
          {`Slide ${index + 1} of ${total}: ${headingOf(entry)}`}
        </p>
      </div>

      <Drawer open={sectionsOpen} side="left" title="Sections" onClose={() => setSectionsOpen(false)} testId="lms-sections-drawer">
        <ol className="space-y-1">
          {deck.sections.map((s, i) => {
            const reachable = player.canReachSlide(s.start);
            const current = entry.sectionIndex === i;
            const ids = deck.slides.slice(s.start, s.start + s.count).filter((d) => d.persisted).map((d) => d.key);
            const seenCount = ids.filter((id) => player.seen.has(id)).length;
            return (
              <li key={s.id}>
                <button
                  type="button"
                  disabled={!reachable}
                  aria-current={current ? 'true' : undefined}
                  onClick={() => {
                    if (player.goToSlide(s.start)) setSectionsOpen(false);
                  }}
                  data-testid="lms-section-link"
                  className={`flex min-h-[44px] w-full items-center gap-2 rounded-lg px-2.5 text-left text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0 sm:py-1.5 ${
                    current
                      ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className="min-w-0 flex-1">{s.title}</span>
                  {!reachable ? (
                    <>
                      <Lock className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                      <span className="sr-only">(answer the check before it first)</span>
                    </>
                  ) : ids.length > 0 ? (
                    <span className="flex-shrink-0 text-xs tabular-nums text-gray-500 dark:text-gray-400">{`${seenCount}/${ids.length}`}</span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ol>
      </Drawer>

      <Drawer open={glossaryOpen} side="right" title="Glossary" onClose={() => setGlossaryOpen(false)} testId="lms-glossary-drawer">
        <dl className="space-y-3">
          {glossary.map((g) => (
            <div key={g.term}>
              <dt className={microLabelCls}>{g.term}</dt>
              <dd className="mt-0.5 text-sm text-gray-700 dark:text-gray-300">{g.definition}</dd>
            </div>
          ))}
        </dl>
      </Drawer>
    </div>
  );
}

export default SlidesStage;
